/**
 * Ampel der Planungsphase — pure Regeln (Automatisierungsplan, Stufe 1.5).
 *
 * Eine Farbe je Projekt fuer Hub-Kachel, Dashboard und Aufgabenliste, mit
 * kurzen Gruenden fuer Tooltip und Liste. Hier wird NICHTS geschrieben und
 * kein Status gewechselt (Entscheid E9): der Fristablauf ist ein Fakt aus
 * `frist_am`, die Feststellung dazu trifft der Koordinator per Knopf
 * „Abgelaufene Fristen feststellen" — die Ampel zaehlt dafuer nur vor.
 *
 * Die Fachregeln werden NICHT nachgebaut, sondern aus `planungLogic.ts`
 * gelesen: `pruefeFreigabe` entscheidet, welche A-Frage gedeckt ist (nur eine
 * VERSENDETE Bedenkenanzeige deckt), `istUeberfaellig` liest die Frist,
 * `pflichtenStand` den dreiwertigen Pflichtstand live aus den Merkmalen
 * (Stufe 1.1), `baueUnterlagenZeilen` die Checkliste. Aendert sich dort eine
 * Regel, folgt die Ampel von selbst.
 *
 * Hausregel: Ausfall darf nie wie Entwarnung aussehen. Diese Funktion liefert
 * deshalb nie `fehler` — das ist der Zustand des Lesens und wird vom Service
 * gesetzt (`planungAmpelService.ts`), sobald eine Abfrage scheitert.
 */

import type {
    PflichtArt,
    Planungsfrage,
    Planungshinweis,
    Planungsphase,
    SigeSchrittDokumente,
} from './types';
import {
    berechneKennzahlen,
    faelligeStatuswechsel,
    fristDatum,
    istUeberfaellig,
    PFLICHT_ART_LABEL,
    pflichtenStand,
    pruefeFreigabe,
    restrisikoliste,
} from './planungLogic';
import { baueUnterlagenZeilen, datumDeutsch, istUnerledigt } from './unterlagenLogic';

// ═══════════════════════════════════════════════════════════════════════════
// VERTRAG
// ═══════════════════════════════════════════════════════════════════════════

/**
 *   grau   = keine Phase angelegt, keine Planungsleistung im LV — nichts zu tun
 *   rot    = Handlungsbedarf (ueberfaellige A-Frage ohne Bedenkenanzeige,
 *            Pflicht ungeklaert und Vorankuendigungsfrist naht)
 *   gelb   = offene Punkte (Fristen laufen oder sind festzustellen,
 *            Unterlagen fehlen, Pflicht ungeklaert)
 *   gruen  = Freigabe moeglich bzw. abgeschlossen, nichts offen
 *   fehler = die Lage konnte nicht gelesen werden — NIE als gruen deuten
 */
export type AmpelFarbe = 'grau' | 'rot' | 'gelb' | 'gruen' | 'fehler';

export interface AmpelGrund {
    farbe: Exclude<AmpelFarbe, 'grau' | 'fehler'>;
    text: string;
}

export interface PlanungAmpel {
    farbe: AmpelFarbe;
    /** Schwerste zuerst; kurz, fuer Tooltip und Liste. */
    gruende: AmpelGrund[];
    /** Klaerungsquote: beantwortet + gegenstandslos / alle Fragen. */
    quote: { geklaert: number; gesamt: number };
    /** Ueberfaellige A-Fragen ohne versendete Bedenkenanzeige. */
    ueberfaelligA: number;
    /** Gestellte Fragen mit abgelaufener Frist, noch nicht festgestellt —
     *  genau die Zeilen, die der Sammelknopf „Abgelaufene Fristen
     *  feststellen" (`markiereNichtBeantwortet`) umstellen wuerde. */
    abgelaufeneFristen: number;
    /** ISO-Tag = Baustart − 14 Tage; `null` ohne Baustart. */
    vorankuendigungFrist: string | null;
}

export interface AmpelEingang {
    phase: Planungsphase | null;
    fragen: Planungsfrage[];
    hinweise: Planungshinweis[];
    /** Heutiger Tag in Ortszeit (`heuteLokalIso`), YYYY-MM-DD. */
    heuteIso: string;
    /** Ausfuehrungsbeginn, Rueckfall Projektstart — bestimmt der Aufrufer. */
    baustartIso: string | null;
    /** `planning_general` im LV. */
    lvHatPlanungsleistung?: boolean;
    /** Mitgeltende Dokumente des SiGe-Plans, sofern der Aufrufer sie hat
     *  (Einzelprojekt). Die Sammelabfrage laesst sie weg — dann zaehlen nur
     *  die aus den Merkmalen erforderlichen Unterlagen. */
    sigeSchritte?: readonly SigeSchrittDokumente[];
}

export const AMPEL_LABEL: Record<AmpelFarbe, string> = {
    grau: 'Keine Planungsphase',
    rot: 'Handlungsbedarf',
    gelb: 'Offene Punkte',
    gruen: 'Geklärt',
    fehler: 'Nicht lesbar',
};

// ═══════════════════════════════════════════════════════════════════════════
// TERMINE
// ═══════════════════════════════════════════════════════════════════════════

/** Vorankuendigung spaetestens zwei Wochen vor Baustart (§ 2 Abs. 2
 *  BaustellV) — dieselbe Regel wie der Kickoff (`projectKickoff.ts`). */
export const VORANK_TAGE_VOR_BAUSTART = 14;

/** So viele Tage vor der Vorankuendigungsfrist wird eine ungeklaerte Pflicht
 *  rot: zwei Wochen, um Zahlen zu beschaffen und die Anzeige zu schreiben. */
export const ROT_FENSTER_TAGE = 14;

const istIsoTag = (s: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(s);
const tagVon = (iso: string | null | undefined): string => String(iso || '').slice(0, 10);

/** Vorankuendigungsfrist als ISO-Tag, `null` ohne brauchbaren Baustart. */
export const vorankuendigungFrist = (baustartIso: string | null | undefined): string | null => {
    const start = tagVon(baustartIso);
    if (!istIsoTag(start)) return null;
    return fristDatum(start, -VORANK_TAGE_VOR_BAUSTART) || null;
};

/** Ganze Tage von `vonIso` bis `bisIso`, negativ wenn `bisIso` zurueckliegt.
 *  Beide Seiten UTC-Mitternacht, damit die Sommerzeit keinen Tag verschluckt. */
const tageBis = (vonIso: string, bisIso: string): number =>
    Math.round((Date.parse(`${bisIso}T00:00:00Z`) - Date.parse(`${vonIso}T00:00:00Z`)) / 86400000);

/** Heute ≥ Vorankuendigungsfrist − 14 Tage (einschliesslich der Grenze und
 *  aller Tage danach — eine ueberschrittene Frist bleibt im Fenster). */
const imRotFenster = (heute: string, frist: string | null): boolean => {
    if (!frist || !istIsoTag(heute)) return false;
    const beginn = fristDatum(frist, -ROT_FENSTER_TAGE);
    return !!beginn && heute >= beginn;
};

const fristText = (heute: string, frist: string): string => {
    const am = datumDeutsch(frist) || frist;
    const tage = tageBis(heute, frist);
    if (tage > 0) return `Vorankündigungsfrist ${am} in ${tage} ${tage === 1 ? 'Tag' : 'Tagen'}`;
    if (tage === 0) return `Vorankündigungsfrist ${am} ist heute`;
    return `Vorankündigungsfrist ${am} seit ${-tage} ${-tage === 1 ? 'Tag' : 'Tagen'} überschritten`;
};

// ═══════════════════════════════════════════════════════════════════════════
// PFLICHTEN
// ═══════════════════════════════════════════════════════════════════════════

/** Die drei Schwellen-Pflichten, die die Ampel rot machen koennen. Die UfsA
 *  haengt am Koordinator (gleicher Stand), der A+S-Verdacht ist keine Pflicht
 *  nach BaustellV — beide zeigt die Hub-Kachel, nicht die Ampel. */
const AMPEL_PFLICHTEN: ReadonlyArray<PflichtArt> = ['vorankuendigung', 'sigeplan', 'koordinator'];

/**
 * Namen der Pflichten mit Stand `ungeklaert` — LIVE aus den Merkmalen ueber
 * `pflichtenStand` (Stufe 1.1), nicht aus dem Schnappschuss `pruefung`: der
 * kann hinter den Merkmalen zurueckliegen, und die Ampel soll den heutigen
 * Stand zeigen. Es ist derselbe Rechenweg, mit dem `schreibeGrundlagen` den
 * Schnappschuss festschreibt — also keine zweite Wahrheit. Ohne Merkmale ist
 * nichts geklaert.
 */
const ungeklaertePflichten = (phase: Planungsphase, heute: string): string[] =>
    pflichtenStand(phase, heute)
        .filter(p => AMPEL_PFLICHTEN.includes(p.art) && p.stand === 'ungeklaert')
        .map(p => PFLICHT_ART_LABEL[p.art]);

// ═══════════════════════════════════════════════════════════════════════════
// AMPEL
// ═══════════════════════════════════════════════════════════════════════════

const zaehlwort = (n: number, einzahl: string, mehrzahl: string): string =>
    `${n} ${n === 1 ? einzahl : mehrzahl}`;

/** „… (davon 2 haftungsrelevant)" — leer, wenn keine A-Frage dabei ist. */
const davonA = (liste: Planungsfrage[]): string => {
    const a = liste.filter(f => f.kritikalitaet === 'A').length;
    return a ? ` (davon ${a} haftungsrelevant)` : '';
};

const unerledigt = (f: Planungsfrage): boolean =>
    f.status !== 'beantwortet' && f.status !== 'gegenstandslos';

const ohnePhase = (frist: string | null): Omit<PlanungAmpel, 'farbe' | 'gruende'> => ({
    quote: { geklaert: 0, gesamt: 0 },
    ueberfaelligA: 0,
    abgelaufeneFristen: 0,
    vorankuendigungFrist: frist,
});

export const planungAmpel = (e: AmpelEingang): PlanungAmpel => {
    const heute = tagVon(e.heuteIso);
    const frist = vorankuendigungFrist(e.baustartIso);
    const fensterOffen = imRotFenster(heute, frist);

    // ── Keine Phase ──────────────────────────────────────────────────────
    if (!e.phase) {
        if (!e.lvHatPlanungsleistung) return { farbe: 'grau', gruende: [], ...ohnePhase(frist) };
        // Planungsleistung verkauft, aber nie angefangen — das stille
        // Nicht-Anfangen (Plan 1.6). Rot, sobald die Vorankuendigungsfrist in
        // Reichweite ist: dann fehlt die Grundlage fuer die Anzeige.
        const farbe: AmpelGrund['farbe'] = fensterOffen ? 'rot' : 'gelb';
        const zusatz = fensterOffen && frist ? ` — ${fristText(heute, frist)}` : '';
        return {
            farbe,
            gruende: [{ farbe, text: `Planungsleistung beauftragt, Planungsphase noch nicht angelegt${zusatz}` }],
            ...ohnePhase(frist),
        };
    }

    const phase = e.phase;
    const fragen = e.fragen || [];
    const hinweise = e.hinweise || [];
    const abgeschlossen = phase.status === 'abgeschlossen';
    const rot: AmpelGrund[] = [];
    const gelb: AmpelGrund[] = [];
    const gruen: AmpelGrund[] = [];

    // ── Fragen ───────────────────────────────────────────────────────────
    const freigabe = pruefeFreigabe(fragen, hinweise);
    const blockiert = new Set(freigabe.blockierendeCodes);
    const offene = fragen.filter(unerledigt);

    // Rot: A-Frage ueber der Frist, die keine VERSENDETE Bedenkenanzeige
    // deckt. `nicht_beantwortet` zaehlt mit — die Feststellung des
    // Koordinators ist mindestens so hart wie der Blick auf die Frist.
    const ueberfaelligA = offene.filter(f => f.kritikalitaet === 'A' && blockiert.has(f.code)
        && (istUeberfaellig(f, heute) || f.status === 'nicht_beantwortet')).length;
    if (ueberfaelligA) {
        rot.push({
            farbe: 'rot',
            text: `${zaehlwort(ueberfaelligA, 'haftungsrelevante Frage', 'haftungsrelevante Fragen')} überfällig ohne versendete Bedenkenanzeige`,
        });
    }

    // Fristablauf ohne Feststellung: Frist vorbei, Status noch nicht
    // `nicht_beantwortet`. `abgelaufeneFristen` zaehlt enger — nur
    // `gestellt`, denn nur die stellt der Sammelknopf um.
    const abgelaufeneFristen = faelligeStatuswechsel(fragen, heute).length;
    const fristVorbei = offene.filter(f => f.status !== 'nicht_beantwortet' && istUeberfaellig(f, heute));
    if (fristVorbei.length) {
        gelb.push({
            farbe: 'gelb',
            text: `${zaehlwort(fristVorbei.length, 'Frist', 'Fristen')} abgelaufen, Ablauf noch nicht festgestellt${davonA(fristVorbei)}`,
        });
    }

    const laufend = offene.filter(f => (f.status === 'gestellt' || f.status === 'teilbeantwortet')
        && !istUeberfaellig(f, heute));
    if (laufend.length) {
        gelb.push({
            farbe: 'gelb',
            text: `${zaehlwort(laufend.length, 'Frage', 'Fragen')} gestellt, Frist läuft${davonA(laufend)}`,
        });
    }

    if (!abgeschlossen) {
        // `pruefeFreigabe` meldet bei leerer Liste „Freigabe moeglich" — fuer
        // das Gate richtig, fuer die Ampel waere es eine Entwarnung ohne
        // Grundlage. Ohne Fragen ist nichts geklaert.
        if (!fragen.length) gelb.push({ farbe: 'gelb', text: 'Noch keine Fragen erzeugt' });
        const nichtGestellt = offene.filter(f => f.status === 'offen');
        if (nichtGestellt.length) {
            gelb.push({
                farbe: 'gelb',
                text: `${zaehlwort(nichtGestellt.length, 'Frage', 'Fragen')} noch nicht gestellt${davonA(nichtGestellt)}`,
            });
        }
    }

    // ── Unterlagen ───────────────────────────────────────────────────────
    // `erforderlich` = Merkmal ausdruecklich wahr oder im SiGe-Plan genannt;
    // „zu klaeren" (Merkmal offen) zaehlt hier nicht — dafuer steht die
    // Frage im Katalog. Offen ODER angefordert: solange sie nicht vorliegt,
    // fehlt sie.
    const zeilen = baueUnterlagenZeilen({
        merkmale: phase.merkmale || {},
        eintraege: phase.unterlagen || [],
        sigeSchritte: e.sigeSchritte || [],
    });
    const unterlagenOffen = zeilen.filter(z => z.erforderlich && istUnerledigt(z.status)).length;
    if (unterlagenOffen) {
        gelb.push({
            farbe: 'gelb',
            text: `${zaehlwort(unterlagenOffen, 'erforderliche Unterlage liegt', 'erforderliche Unterlagen liegen')} noch nicht vor`,
        });
    }

    // ── Pflichten ────────────────────────────────────────────────────────
    // Rot nur im Fenster vor der Vorankuendigungsfrist — und nie nach dem
    // Abschluss. Der Abschluss (`freigeben` in PlanungModul.tsx) prueft nur
    // `pruefeFreigabe` und laesst den Koordinator die Restrisikoliste
    // bestaetigen; die Schwellenwertpruefung ist dort keine Bedingung. Eine
    // danach noch ungeklaerte Pflicht ist also Teil der dokumentierten
    // Uebergabe in die Ausfuehrungsphase und kein neuer Alarm: sie bleibt als
    // gelber Restpunkt sichtbar, wird aber nicht rot, nur weil der Baustart
    // naeher rueckt. Das Mahnen zur Vorankuendigung uebernimmt dort der
    // Kickoff (`projectKickoff.ts`, „Frist ueberschritten").
    const pflichten = ungeklaertePflichten(phase, heute);
    if (pflichten.length) {
        const liste = pflichten.join(', ');
        if (!abgeschlossen && fensterOffen && frist) {
            rot.push({ farbe: 'rot', text: `Pflicht ungeklärt (${liste}) — ${fristText(heute, frist)}` });
        } else {
            gelb.push({ farbe: 'gelb', text: `Pflicht noch ungeklärt: ${liste}` });
        }
    }

    // ── Gruen ────────────────────────────────────────────────────────────
    if (abgeschlossen) {
        // Restpunkte sind dokumentierte Uebergabe, kein offener Posten —
        // deshalb als Information und nicht als gelber Grund.
        const rest = restrisikoliste(fragen, hinweise, heute).length;
        gruen.push({
            farbe: 'gruen',
            text: rest
                ? `Abgeschlossen, ${zaehlwort(rest, 'Restpunkt', 'Restpunkte')} in der Restrisikoliste`
                : 'Abgeschlossen ohne Restpunkte',
        });
    } else if (!rot.length && !gelb.length) {
        gruen.push({
            farbe: 'gruen',
            text: freigabe.gedeckteCodes.length
                ? `Freigabe möglich, ${zaehlwort(freigabe.gedeckteCodes.length, 'haftungsrelevante Frage', 'haftungsrelevante Fragen')} durch Bedenkenanzeige gedeckt`
                : 'Freigabe möglich, Pflichten und Unterlagen geklärt',
        });
    }

    const kennzahlen = berechneKennzahlen(fragen, heute);
    return {
        farbe: rot.length ? 'rot' : gelb.length ? 'gelb' : 'gruen',
        gruende: [...rot, ...gelb, ...gruen],
        quote: { geklaert: kennzahlen.beantwortet + kennzahlen.gegenstandslos, gesamt: kennzahlen.gesamt },
        ueberfaelligA,
        abgelaufeneFristen,
        vorankuendigungFrist: frist,
    };
};
